import React, { useState, useMemo, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Plus } from "lucide-react";
import { formatCurrency } from "@/modules/quotesDeprecated/QuotePreview/utils";
import { Skeleton } from "@/components/ui/skeleton";
import {
  PaginationFooter,
  type PaginationConfig,
} from "@/components/ui/pagination-footer";
import { formatDateOnly } from "@forgepoint/shared";

export type ColumnType =
  | "string"
  | "currency"
  | "number"
  | "date"
  | "dateOnly"
  | "badge"
  | "link";

export interface DataTableColumn<T> {
  key: keyof T | string;
  header: React.ReactNode;
  type?: ColumnType;
  width?: string | number;
  align?: "left" | "center" | "right";
  format?: (value: unknown, row: T) => React.ReactNode;
  className?: string;
}

export type { PaginationConfig };

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  onClickRow?: (row: T, index: number) => void;
  onChangeSearch?: (value: string) => void;
  searchPlaceholder?: string;
  className?: string;
  isLoading?: boolean;
  onCreate?: () => void;
  createButtonText?: string;
  hideSearch?: boolean;
  pagination?: PaginationConfig;
  toolbarActions?: React.ReactNode;
  emptyState?: React.ReactNode;
}

function formatValue<T>(
  value: unknown,
  type: ColumnType = "string",
  formatter?: (value: unknown, row: T) => React.ReactNode,
  row?: T,
): React.ReactNode {
  if (formatter && row) {
    return formatter(value, row);
  }

  if (value === null || value === undefined) {
    return "-";
  }

  switch (type) {
    case "currency":
      if (typeof value === "string") {
        return formatCurrency(value);
      }
      return String(value);
    case "number":
      if (typeof value === "number") {
        return new Intl.NumberFormat("en-US").format(value);
      }
      return String(value);

    case "date":
      if (value instanceof Date) {
        return value.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      }
      if (typeof value === "string") {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          });
        }
      }
      return String(value);

    case "dateOnly":
      if (typeof value === "string") {
        return (
          formatDateOnly(
            value,
            { year: "numeric", month: "short", day: "numeric" },
            "en-US",
          ) ?? String(value)
        );
      }
      return String(value);

    case "badge":
      return (
        <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
          {String(value)}
        </span>
      );

    case "link":
      if (typeof value === "string" && value.startsWith("http")) {
        return (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-info hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {value}
          </a>
        );
      }
      return String(value);

    default:
      return String(value);
  }
}

function getNestedValue<T>(obj: T, path: string): unknown {
  const keys = path.split(".");
  let result: unknown = obj;

  for (const key of keys) {
    if (result && typeof result === "object" && key in result) {
      result = (result as Record<string, unknown>)[key];
    } else {
      return undefined;
    }
  }

  return result;
}

function LoadingRows<T>({
  columns,
  rows = 10,
}: {
  columns: DataTableColumn<T>[];
  rows?: number;
}) {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <tr key={rowIndex} className="bg-background border-b border-border">
          {columns.map((column, colIndex) => (
            <td
              key={String(column.key)}
              className={cn(
                "px-4 py-3",
                colIndex > 0 && "border-l border-border",
              )}
            >
              <Skeleton className="h-4 w-full max-w-[200px]" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export function DataTable<T extends Record<string, unknown>>({
  columns,
  data,
  onClickRow,
  onChangeSearch,
  searchPlaceholder = "Search...",
  className,
  isLoading = false,
  onCreate,
  createButtonText = "Create",
  hideSearch = false,
  pagination,
  toolbarActions,
  emptyState,
}: DataTableProps<T>) {
  const [searchValue, setSearchValue] = useState("");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const paginationPage = pagination?.page;

  // Reset the scroll position to the top whenever the page changes, so a new
  // page opens at its first row rather than wherever the previous page was
  // scrolled.
  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0 });
  }, [paginationPage]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    onChangeSearch?.(value);
  };

  const filteredData = useMemo(() => {
    if (onChangeSearch || !searchValue) {
      return data;
    }

    const searchLower = searchValue.toLowerCase();
    return data.filter((row) => {
      return columns.some((column) => {
        const value = getNestedValue(row, String(column.key));
        if (value === null || value === undefined) {
          return false;
        }
        return String(value).toLowerCase().includes(searchLower);
      });
    });
  }, [data, searchValue, columns, onChangeSearch]);

  const getAlignClass = (align?: "left" | "center" | "right") => {
    switch (align) {
      case "center":
        return "text-center";
      case "right":
        return "text-right";
      default:
        return "text-left";
    }
  };

  return (
    <div className={cn("flex flex-col h-full w-full", className)}>
      {(!hideSearch || onCreate || toolbarActions) && (
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border bg-background">
          {!hideSearch ? (
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9 h-8 border-input focus:border-ring rounded-md"
              />
            </div>
          ) : (
            <div className="flex-1" />
          )}
          <div className="flex items-center gap-2">
            {toolbarActions}
            {onCreate && (
              <Button onClick={onCreate} size="sm">
                <Plus className="h-4 w-4 mr-1" />
                {createButtonText}
              </Button>
            )}
          </div>
        </div>
      )}

      <div ref={scrollContainerRef} className="flex-1 overflow-auto">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10 bg-muted/95 backdrop-blur supports-[backdrop-filter]:bg-muted/75">
            <tr>
              {columns.map((column, index) => (
                <th
                  key={String(column.key)}
                  style={{ width: column.width }}
                  className={cn(
                    "px-2 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider border-b border-border",
                    index > 0 && "border-l border-border",
                    getAlignClass(column.align),
                    column.className,
                  )}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <LoadingRows columns={columns} />
            ) : filteredData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-2 py-4 text-center text-muted-foreground"
                >
                  {emptyState ?? "No data found"}
                </td>
              </tr>
            ) : (
              filteredData.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  onClick={() => onClickRow?.(row, rowIndex)}
                  className={cn(
                    "group bg-background border-b border-border transition-colors",
                    onClickRow && "cursor-pointer hover:bg-muted/50",
                  )}
                >
                  {columns.map((column, colIndex) => {
                    const value = getNestedValue(row, String(column.key));
                    return (
                      <td
                        key={String(column.key)}
                        className={cn(
                          "px-2 py-1 text-sm text-foreground",
                          colIndex > 0 && "border-l border-border",
                          getAlignClass(column.align),
                          column.className,
                        )}
                      >
                        {formatValue(value, column.type, column.format, row)}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {pagination && pagination.total > 0 && (
        <PaginationFooter pagination={pagination} />
      )}
    </div>
  );
}
